import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchUser } from '../../services/api/auth';
import { store } from '../store';
import { destroyCookie, getCookie, setCookie } from '../../utils/cookie';
import { PURGE } from 'redux-persist';
import { getToken, removeToken, setToken } from '../../utils/authStorage';
import { apiConfigs } from '../../constants/apiConfigs';
import { isFresh } from '../../utils/helpers';
import { getBackendTokenForFirebase } from '../../services/api/chat';

interface Configurations{
    ig_auth_wall: string,
    max_social_accounts: {
        [key: string]: number
    }
}

// UserData Interface
export interface UserData {
    user: User,
    master: Master
    configurations?: Configurations
}

// User Interface
export interface User {
    uuid: string;
    name: string;
    last_login_at: string | null;
    last_login_ip: string | null;
    profile_photo_url: string;
    username: string;
    user_verified_at: string;
    access_type: string;
    is_test: boolean;
    subscription: Subscription;
    referral_code: string;
    tickers?: Ticker[]
}

export interface Ticker {
    color: string,
    description: string,
    title: string,
    url: string
}

// Subscription Interface
export interface Subscription {
    applications_applicable_limit: number;
    applications_consumed_limit: number;
    applications_color: string;
    active_plan_name: string;
}

// Master Interface
export interface Master {
    gender_list: Record<string, string>;
    niche_list: Record<string, string>;
    language_list: Record<string, string>;
}

// AuthState Interface
interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    token: string | null;
    loading: boolean;
    error: string | null;
    master: Master | null;
    configurations?: Configurations;
    authInitialized: boolean;
    firebaseToken: {
        token: string,
        generatedAt: number
    } | null
}

const initialState: AuthState = {
    user: null,
    isAuthenticated:false,
    token: null,
    loading: true,
    error: null,
    master: null,
    authInitialized: false,
    firebaseToken: null
};

export const initAuth = createAsyncThunk(
    'auth/init',
    async (_, { dispatch }) => {
      const token = await getToken();
      if (token) {
        await dispatch(login(token));
      }
    }
  );
  

export const login = createAsyncThunk<{ userData: UserData, token: string }, string, { rejectValue: string }>(
    'auth/login',
    async (token, { rejectWithValue }) => {
        try {
            const userData = await fetchUser(token);
            // Save token in a secure cookie
            setToken(token);
            return { userData, token };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to authenticate');
        }
    }
);

export const fetchFirebaseBackendTokenThunk = createAsyncThunk<{token: string}, void, {rejectValue: string}>(
    'auth/firebase-token',
    async (_, {rejectWithValue, getState}) => {
        try {
            const state = getState() as {auth: AuthState};
            const STALE_TIME = apiConfigs.CREATE_FIREBASE_TOKEN.staleTime;
            if(state.auth.firebaseToken && isFresh(state.auth.firebaseToken?.generatedAt, STALE_TIME)){
                return {token: state.auth.firebaseToken.token};
            }
            const token: string = await getBackendTokenForFirebase();
            return {token};            
        } catch (error) {
            return rejectWithValue('Failed to login to firebase!');
        }
    }
)

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.token = null;
            state.error = null;
            // Clear token from cookies
            removeToken();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action: PayloadAction<{ userData: UserData, token: string }>) => {
                state.loading = false;
                state.user = action.payload.userData.user;
                state.master = action.payload.userData.master;
                if(action.payload.userData.configurations){
                    state.configurations = action.payload.userData.configurations
                }
                state.isAuthenticated = true;
                state.error = null;
                state.token = action.payload.token;
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Login failed';
            })
            .addCase(initAuth.fulfilled, (state) => {
                state.authInitialized = true;
            })
            .addCase(fetchFirebaseBackendTokenThunk.fulfilled, (state, {payload}) => {
                state.firebaseToken = {
                    token: payload.token,
                    generatedAt: Date.now()
                };
            })
            .addCase(PURGE, () => {
                return initialState;
            });
    },
});

export const { logout } = authSlice.actions;
export type AppDispatch = typeof store.dispatch;
export default authSlice.reducer;